import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ExportModal = ({ onClose, onExport }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    dateRange: 'all',
    fields: {
      companyName: true,
      contactName: true,
      email: true,
      phone: true,
      planType: true,
      subscriptionStatus: true,
      mrr: true,
      lastActivity: true,
      registrationDate: true,
      activityLevel: false
    }
  });

  const handleFieldToggle = (field) => {
    setExportConfig({
      ...exportConfig,
      fields: {
        ...exportConfig?.fields,
        [field]: !exportConfig?.fields?.[field]
      }
    });
  };

  const handleExport = () => {
    onExport(exportConfig);
  };

  const fieldLabels = {
    companyName: 'Company Name',
    contactName: 'Contact Name',
    email: 'Email Address',
    phone: 'Phone Number',
    planType: 'Plan Type',
    subscriptionStatus: 'Subscription Status',
    mrr: 'Monthly Recurring Revenue',
    lastActivity: 'Last Activity Date',
    registrationDate: 'Registration Date',
    activityLevel: 'Activity Level'
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg elevation-lg w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground font-heading">Export Client Data</h2>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Customize your export with selected fields and date ranges
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <label className="text-sm font-medium text-foreground font-body mb-2 block">
              Export Format
            </label>
            <div className="grid grid-cols-3 gap-3">
              {['csv', 'excel', 'pdf']?.map((format) => (
                <button
                  key={format}
                  onClick={() => setExportConfig({ ...exportConfig, format })}
                  className={`px-4 py-3 rounded-md border transition-smooth text-sm font-medium font-body ${
                    exportConfig?.format === format
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                  }`}
                >
                  {format?.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="text-sm font-medium text-foreground font-body mb-2 block">
              Date Range
            </label>
            <Select
              value={exportConfig?.dateRange}
              onChange={(e) => setExportConfig({ ...exportConfig, dateRange: e?.target?.value })}
              className="w-full"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last90days">Last 90 Days</option>
              <option value="lastyear">Last Year</option>
              <option value="custom">Custom Range</option>
            </Select>
          </div>

          {/* Field Selection */}
          <div>
            <label className="text-sm font-medium text-foreground font-body mb-3 block">
              Select Fields to Export
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto p-4 bg-muted/30 rounded-lg">
              {Object.entries(exportConfig?.fields)?.map(([field, checked]) => (
                <label
                  key={field}
                  className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-smooth"
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => handleFieldToggle(field)}
                  />
                  <span className="text-sm text-foreground font-body">{fieldLabels?.[field]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-foreground font-body">
              <Icon name="Info" size={16} className="text-primary" />
              <span>
                Exporting {Object.values(exportConfig?.fields)?.filter(Boolean)?.length} fields in {exportConfig?.format?.toUpperCase()} format
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-card border border-border text-foreground rounded-md hover:bg-muted transition-smooth text-sm font-medium font-body"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth text-sm font-medium font-body"
          >
            <Icon name="Download" size={16} />
            Export Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;